// Resume46.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  LinkedIn as LinkedInIcon,
  Language as LanguageIcon,
  DriveEta as DriveEtaIcon,
} from '@mui/icons-material';

// Default section order
const defaultSectionOrder = [
  'summary',
  'experience',
  'education',
  'certifications',
  'skills',
  'languages',
  'accomplishments',
  'additionalInfo',
  'softwareSkills',
  'volunteering',
  'interests',
  'websites',
  'references',
];

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
};

const pageSizeMap = {
  A4: { width: '210mm', minHeight: '297mm', height: '297mm' },
  Letter: { width: '216mm', minHeight: '279mm', height: '279mm' },
  Legal: { width: '216mm', minHeight: '356mm', height: '356mm' },
  A3: { width: '297mm', minHeight: '420mm', height: '420mm' },
  Executive: { width: '184mm', minHeight: '267mm', height: '267mm' },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const Resume46 = ({
  theme = "#1e88e5",
  color = "#1e88e5",
  font = "Georgia",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "18px",
  sectionSpacing = 30,
  paragraphSpacing = 12,
  lineSpacing = 22,
  topBottomMargin = 30,
  sideMargins = 30,
  pageSize = "A4",
  formData = {},
  exporting = false,
  enableDragDrop = true,
}) => {
  const [themeColor, setThemeColor] = useState(color || theme);
  const [currentPage, setCurrentPage] = useState(0);
  const resumeRef = useRef(null);
  const [totalPages, setTotalPages] = useState(1);
  const [pageBreaks, setPageBreaks] = useState([]);
  const contentRef = useRef(null);

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  // ── State for all data sections ─────────────────────────────────────────
  const [personalData, setPersonalData]             = useState({});
  const [savedSummary, setSavedSummary]             = useState('');
  const [savedSkills, setSavedSkills]               = useState([]);
  const [educationEntries, setEducationEntries]     = useState([]);
  const [workExperiences, setWorkExperiences]       = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedLanguages, setSavedLanguages]         = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedSoftware, setSavedSoftware]           = useState([]);
  const [savedReferences, setSavedReferences]       = useState([]);
  const [savedVolunteering, setSavedVolunteering]   = useState([]);
  const [savedInterests, setSavedInterests]         = useState([]);
  const [savedWebsites, setSavedWebsites]           = useState([]);
  const [customSections, setCustomSections]         = useState({});

  // Section ordering states
  const [sectionOrder, setSectionOrder]         = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex]       = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ── Global styles ───────────────────────────────────────────────────────
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  // Common overflow-safe style spread onto every text element
  const safe = {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
  };

  // ── Load all data from localStorage ────────────────────────────────────
  useEffect(() => {
    setThemeColor(color || theme);

    // Personal data
    let personal = {};
    const storedPersonalData = localStorage.getItem("personalData");
    if (storedPersonalData) {
      try { personal = JSON.parse(storedPersonalData); } catch {}
    }
    if (formData && Object.keys(formData).length > 0) {
      personal = { ...personal, ...formData };
    }
    setPersonalData(personal);

    // Summary
    const storedSummaries = localStorage.getItem("summaries");
    if (storedSummaries) {
      try {
        const summaries = JSON.parse(storedSummaries);
        if (Array.isArray(summaries) && summaries.length > 0)
          setSavedSummary(summaries[summaries.length - 1]);
      } catch {}
    }

    // Education
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) { try { setEducationEntries(JSON.parse(storedEntries)); } catch {} }

    // Skills
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) { try { setSavedSkills(JSON.parse(storedSkills)); } catch {} }

    // Work experiences
    const storedWork = localStorage.getItem("workExperiences");
    if (storedWork) { try { setWorkExperiences(JSON.parse(storedWork) || []); } catch {} }

    // Websites
    const websites = localStorage.getItem("websites");
    if (websites) { try { setSavedWebsites(JSON.parse(websites) || []); } catch {} }

    // Certifications
    const certifications = localStorage.getItem("certificationsList");
    if (certifications) { try { setSavedCertifications(JSON.parse(certifications) || []); } catch {} }

    // Languages
    const languages = localStorage.getItem("languagesList");
    if (languages) { try { setSavedLanguages(JSON.parse(languages) || []); } catch {} }

    // Accomplishments
    const accomplishments = localStorage.getItem("accomplishmentsList");
    if (accomplishments) { try { setSavedAccomplishments(JSON.parse(accomplishments) || []); } catch {} }

    // Additional info
    const additionalInfo = localStorage.getItem("additionalInfo");
    if (additionalInfo) { try { setSavedAdditionalInfo(JSON.parse(additionalInfo) || []); } catch {} }

    // Software skills
    const software = localStorage.getItem("softwareSkills");
    if (software) { try { setSavedSoftware(JSON.parse(software) || []); } catch {} }

    // References
    const references = localStorage.getItem("referencesList");
    if (references) { try { setSavedReferences(JSON.parse(references) || []); } catch {} }

    // Volunteering
    const volunteering = localStorage.getItem("volunteering");
    if (volunteering) { try { setSavedVolunteering(JSON.parse(volunteering) || []); } catch {} }

    // Interests
    const interests = localStorage.getItem("interests");
    if (interests) { try { setSavedInterests(JSON.parse(interests) || []); } catch {} }

    // Custom sections
    const userId = localStorage.getItem("userId");
    let sectionsData = {};
    if (userId) {
      const currentCustomSections = localStorage.getItem(`current_custom_sections_${userId}`);
      if (currentCustomSections) {
        try {
          const customSectionsList = JSON.parse(currentCustomSections);
          if (Array.isArray(customSectionsList)) {
            customSectionsList.forEach(sectionName => {
              const key = `custom_${userId}_${sectionName}`;
              try {
                const raw = localStorage.getItem(key);
                if (raw) {
                  const parsed = JSON.parse(raw);
                  const validItems = (parsed.items || []).filter(item => item && item.trim() !== '');
                  if (validItems.length > 0) sectionsData[sectionName] = validItems;
                }
              } catch {}
            });
          }
        } catch {}
      }
    }
    setCustomSections(sectionsData);

    // Section order
    const savedSectionOrder = localStorage.getItem("resume46SectionOrder");
    const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);
    if (savedSectionOrder) {
      try {
        const parsedOrder = JSON.parse(savedSectionOrder);
        const base = parsedOrder.filter(s => !s.startsWith('custom_'));
        // Append any new default sections not in saved order
        defaultSectionOrder.forEach(s => { if (!base.includes(s)) base.push(s); });
        setSectionOrder([...base, ...currentCustomIds]);
      } catch {
        setSectionOrder([...defaultSectionOrder, ...currentCustomIds]);
      }
    } else {
      setSectionOrder([...defaultSectionOrder, ...currentCustomIds]);
    }
  }, [color, theme, formData]);

  // Persist section order
  useEffect(() => {
    if (sectionOrder.length > 0)
      localStorage.setItem("resume46SectionOrder", JSON.stringify(sectionOrder));
  }, [sectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ── Drag & drop handlers ────────────────────────────────────────────────
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };
  const handleDragOver  = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
  const handleDragLeave = () => setDragOverIndex(null);
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const { index: source } = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (source !== targetIndex) {
      const newOrder = [...sectionOrder];
      const [moved] = newOrder.splice(source, 1);
      newOrder.splice(targetIndex, 0, moved);
      setSectionOrder(newOrder);
    }
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };
  const handleDragEnd = (e) => { setDragOverIndex(null); e.currentTarget.style.opacity = '1'; };
  const resetSectionOrder = () => {
    setSectionOrder(defaultSectionOrder);
    localStorage.removeItem("resume46SectionOrder");
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      summary:        'Career Objective',
      skills:         'Additional Skills',
      experience:     'Work Experience',
      education:      'Education',
      certifications: 'Certifications',
      languages:      'Languages',
      accomplishments:'Accomplishments',
      additionalInfo: 'Additional Information',
      softwareSkills: 'Software Skills',
      volunteering:   'Volunteering',
      interests:      'Interests',
      websites:       'Websites & Profiles',
      references:     'References',
    };
    if (sectionId.startsWith('custom_'))
      return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId.toUpperCase();
  };

  // ── Section title shared style ──────────────────────────────────────────
  const titleSx = {
    fontWeight: 700,
    color: '#000',
    mb: 2,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    ...globalFontStyle,
    ...safe,
    fontSize: headingSize,
  };

  // ── Render section content ──────────────────────────────────────────────
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {

      case 'summary':
        return savedSummary ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Career Objective</Typography>
            <Typography sx={{ lineHeight: 1.8, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedSummary}
            </Typography>
          </Box>
        ) : null;

      case 'skills':
        return savedSkills.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Additional Skills</Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedSkills.map((skill, i) => (
                <li key={i} style={{ ...safe }}>
                  {typeof skill === 'string' ? skill : skill.name || skill.skillName || ''}
                </li>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'experience':
        return workExperiences.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Work Experience</Typography>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, ...safe }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: themeColor, ...globalFontStyle, ...safe }}>
                    {work.jobTitle || work.position || work.role}
                  </Typography>
                  <Typography sx={{ fontStyle: 'italic', color: '#555', ...globalFontStyle, ...safe }}>
                    {formatDate(work.startMonth, work.startYear)} – {(work.current || work.currentlyWorking) ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#333', mb: 1, ...globalFontStyle, ...safe }}>
                  {work.employer || work.company || work.companyName}
                  {work.location && ` | ${work.location}`}
                </Typography>
                {work.description && (
                  <Box sx={{ lineHeight: 1.9, ...globalFontStyle, ...safe }}>
                    {(work.description || '').split('\n').map((line, idx) =>
                      line.trim().startsWith('•') ? (
                        <Box component="ul" key={idx} sx={{ pl: 3, m: 0, ...safe }}>
                          <li style={{ ...safe }}>{line.replace('•', '').trim()}</li>
                        </Box>
                      ) : (
                        <Typography key={idx} sx={{ ...globalFontStyle, ...safe }}>{line}</Typography>
                      )
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Education</Typography>
            {educationEntries.map((edu, i) => {
              const institutionName = edu.educationLevel === "school" ? edu.schoolName : (edu.collegeName || edu.schoolName || edu.institution || edu.universityName);
              const degreeName      = edu.educationLevel === "school" ? edu.standard   : (edu.degreeName || edu.degree || edu.qualification);
              const fieldOfStudy    = edu.educationLevel === "school" ? edu.stream     : (edu.specialization || edu.fieldOfStudy);
              return (
                <Box key={i} sx={{ mb: `${paragraphSpacing}px`, ...safe }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: themeColor, ...globalFontStyle, ...safe }}>
                      {degreeName}{fieldOfStudy ? ` in ${fieldOfStudy}` : ''}
                    </Typography>
                    <Typography sx={{ fontStyle: 'italic', color: '#555', ...globalFontStyle, ...safe }}>
                      {edu.cgpa && `${edu.cgpa} CGPA`}
                      {edu.percentage && ` ${edu.percentage}%`}
                      {edu.grade && ` ${edu.grade}`}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: '#333', ...globalFontStyle, ...safe }}>
                    {institutionName}
                    {edu.schoolLocation || edu.collegeLocation ? `, ${edu.schoolLocation || edu.collegeLocation}` : ''}
                  </Typography>
                  <Typography sx={{ color: '#555', fontStyle: 'italic', ...globalFontStyle, ...safe }}>
                    {formatDate(edu.gradMonth, edu.gradYear) || formatDate(edu.endMonth, edu.endYear) || edu.gradYear || ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Certifications</Typography>
            {savedCertifications.map((cert, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, ...safe }}>
                <Typography sx={{ fontWeight: 700, color: themeColor, ...globalFontStyle, ...safe }}>{cert.name}</Typography>
                <Typography sx={{ color: '#555', fontStyle: 'italic', ...globalFontStyle, ...safe }}>
                  {cert.provider}{cert.provider && cert.year ? ' • ' : ''}{cert.year}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Languages</Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedLanguages.map((lang, i) => (
                <li key={i} style={{ ...safe }}>
                  {typeof lang === 'string' ? lang : lang.name + (lang.level ? ` - ${lang.level}` : '')}
                </li>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Accomplishments</Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedAccomplishments.map((acc, i) => (
                <li key={i} style={{ ...safe }}>{acc}</li>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Additional Information</Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedAdditionalInfo.map((info, i) => (
                <li key={i} style={{ ...safe }}>{info}</li>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'softwareSkills':
        return savedSoftware.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Software Skills</Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedSoftware.map((sw, i) => (
                <li key={i} style={{ ...safe }}>{sw.name} — {sw.level}%</li>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Volunteering</Typography>
            {savedVolunteering.map((vol, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, ...safe }}>
                <Typography sx={{ fontWeight: 700, ...globalFontStyle, ...safe }}>{vol.subtopic}</Typography>
                {(vol.fromDate || vol.toDate) && (
                  <Typography sx={{ fontStyle: 'italic', color: '#555', ...globalFontStyle, ...safe }}>
                    {vol.fromDate} - {vol.toDate}
                  </Typography>
                )}
                {vol.content && (
                  <Typography sx={{ lineHeight: 1.8, whiteSpace: 'pre-line', ...globalFontStyle, ...safe }}>
                    {vol.content}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Interests</Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedInterests.map((interest, i) => (
                <li key={i} style={{ ...safe }}>
                  {typeof interest === 'string' ? interest : interest.name || ''}
                </li>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>Websites & Profiles</Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
              {savedWebsites.map((site, i) => (
                <li key={i} style={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                  {site.url || site}
                </li>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
            <Typography variant="h6" sx={titleSx}>References</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '40px', ...safe }}>
              {savedReferences.map((ref, i) => {
                // Handle both plain string refs and object refs
                if (typeof ref === 'string') {
                  return (
                    <Typography key={i} sx={{ whiteSpace: 'pre-line', ...globalFontStyle, ...safe }}>
                      {ref}
                    </Typography>
                  );
                }
                return (
                  <Box key={i} sx={{ minWidth: 0, ...safe }}>
                    <Typography sx={{ fontWeight: 700, ...globalFontStyle, ...safe }}>
                      {ref.refName || ref.name || ref.referenceName}
                    </Typography>
                    <Typography sx={{ ...globalFontStyle, ...safe }}>
                      {ref.company || ref.companyName}
                      {(ref.position || ref.jobTitle) && ` / ${ref.position || ref.jobTitle}`}
                    </Typography>
                    {ref.phone && (
                      <Typography sx={{ ...globalFontStyle, ...safe }}>Phone: {ref.phone}</Typography>
                    )}
                    {ref.email && (
                      <Typography sx={{ ...globalFontStyle, wordBreak: 'break-all' }}>Email: {ref.email}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = (customSections[customSectionName] || []).filter(i => i && i.trim() !== '');
          if (!items.length) return null;
          return (
            <Box className="resume46-section" sx={{ mb: `${sectionSpacing}px`, ...safe }}>
              <Typography variant="h6" sx={titleSx}>
                {customSectionName.replace(/_/g, ' ').toUpperCase()}
              </Typography>
              <Box component="ul" sx={{ pl: 3, lineHeight: 2.1, mb: `${paragraphSpacing}px`, ...globalFontStyle, ...safe }}>
                {items.map((item, i) => (
                  <li key={i} style={{ ...safe }}>{item}</li>
                ))}
              </Box>
            </Box>
          );
        }
        return null;
    }
  };

  // ── Drag panel inside dialog ────────────────────────────────────────────
  const renderDragPanel = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Drag and drop to reorder sections.
      </Typography>
      <Box sx={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sectionOrder.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e)  => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e)      => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1,
              display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index ? 2 : 1,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              transition: 'all 0.2s ease',
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        ))}
      </Box>
      <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset Section Order
      </Button>
    </Box>
  );

  // ── Main render ─────────────────────────────────────────────────────────
  const renderHeader = () => (
    <Box sx={{ textAlign: 'center', mb: 4 }}>
      <Typography
        variant="h3"
        component="h1"
        sx={{
          fontWeight: 900,
          letterSpacing: '3px',
          color: '#000',
          fontSize: '2.5rem',
          lineHeight: 1.1,
          mb: 1,
          ...globalFontStyle,
          ...safe,
        }}
      >
        {(personalData.firstName?.toUpperCase() || '')} {(personalData.lastName?.toUpperCase() || '')}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#333',
          letterSpacing: '1.5px',
          fontSize: '1.25rem',
          textTransform: 'uppercase',
          ...globalFontStyle,
          ...safe,
        }}
      >
        {personalData.currentPosition || ''}
      </Typography>

      {/* Contact row 1 */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', ...globalFontStyle, color: '#444' }}>
        {personalData.city && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...safe }}>
            <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
            <span>{personalData.city}{personalData.country ? `, ${personalData.country}` : ''}</span>
          </Box>
        )}
        {personalData.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...safe }}>
            <PhoneIcon sx={{ fontSize: 16, color: '#666' }} />
            <span>{personalData.phone}</span>
          </Box>
        )}
        {personalData.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-all' }}>
            <EmailIcon sx={{ fontSize: 16, color: '#666' }} />
            <span>{personalData.email}</span>
          </Box>
        )}
      </Box>

      {/* Contact row 2 — LinkedIn, Website, Driving Licence */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 1, color: '#444', ...globalFontStyle }}>
        {personalData.linkedIn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...safe }}>
            <LinkedInIcon sx={{ fontSize: 16, color: '#0077b5' }} />
            <a
              href={personalData.linkedIn.startsWith('http') ? personalData.linkedIn : `https://${personalData.linkedIn}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#0077b5', textDecoration: 'none', fontFamily: font, fontSize }}
            >
              {personalData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
            </a>
          </Box>
        )}
        {personalData.website && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...safe }}>
            <LanguageIcon sx={{ fontSize: 16, color: '#1976d2' }} />
            <a
              href={personalData.website.startsWith('http') ? personalData.website : `https://${personalData.website}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'none', fontFamily: font, fontSize, wordBreak: 'break-all' }}
            >
              {personalData.website.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          </Box>
        )}
        {personalData.drivingLicence && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...safe }}>
            <DriveEtaIcon sx={{ fontSize: 16, color: '#4caf50' }} />
            <span style={{ fontFamily: font, fontSize }}>DL: {personalData.drivingLicence}</span>
          </Box>
        )}
      </Box>
    </Box>
  );

  const getPageSections = (pageIdx) => {
    if (totalPages === 1 || pageBreaks.length === 0) {
      return sectionOrder.filter(id => renderSectionContent(id) !== null);
    }

    const container = contentRef.current;
    if (!container) return sectionOrder.filter(id => renderSectionContent(id) !== null);

    const sections = container.querySelectorAll(".resume46-section");
    const pageHeight = pagePx.height - topBottomMargin * 2;
    
    const headerEl = container.querySelector(".resume-header-area");
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 180;
    const dividerHeight = 30;
    const headerTotal = headerHeight + dividerHeight;

    let accumulated = 0;
    let currentIdx = 0;
    const pageChunks = [[]];
    const visibleSectionIds = sectionOrder.filter(id => renderSectionContent(id) !== null);

    sections.forEach((section, i) => {
      const h = section.getBoundingClientRect().height;
      const secId = visibleSectionIds[i];
      if (!secId) return;

      const currentLimit = pageHeight - (currentIdx === 0 ? headerTotal : 0);
      
      if (accumulated + h > currentLimit && pageChunks[currentIdx].length > 0) {
        currentIdx++;
        pageChunks.push([]);
        accumulated = 0;
      }
      pageChunks[currentIdx].push(secId);
      accumulated += h;
    });

    return pageChunks[pageIdx] || [];
  };

  const getSectionsForCurrentPage = () => {
    return getPageSections(currentPage);
  };

  // ── Calculate totalPages and pageBreaks ───────────────────────────────────
  useEffect(() => {
    if (exporting) return;
    const container = contentRef.current;
    if (!container) return;

    const id = setTimeout(() => {
      const sections = Array.from(container.querySelectorAll(".resume46-section"));
      const pageHeight = pagePx.height - (topBottomMargin) * 2;

      const headerEl = container.querySelector(".resume-header-area");
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 180;
      const dividerHeight = 30;
      const firstPageHeaderSpacing = headerHeight + dividerHeight;

      const breaks = [];
      let accumulated = 0;
      let pageIndex = 1;

      sections.forEach((section) => {
        const h = section.getBoundingClientRect().height;
        const currentLimit = pageHeight * pageIndex - (pageIndex === 1 ? firstPageHeaderSpacing : 0);
        
        if (accumulated + h > currentLimit) {
          breaks.push(pageIndex);
          pageIndex++;
        }
        accumulated += h;
      });

      const total = Math.max(1, pageIndex);
      setTotalPages(total);
      setPageBreaks(breaks);
    }, 400);

    return () => clearTimeout(id);
  }, [
    sectionOrder, savedSummary, workExperiences, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, savedSoftware, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections,
    topBottomMargin, pagePx.height, exporting,
  ]);

  // Clamp currentPage if totalPages shrinks
  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />}>
                Arrange Sections
              </Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary">
                Reset Sections
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container */}
      <Box
        ref={contentRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: page.width,
          p: `${topBottomMargin}px ${sideMargins}px`,
          boxSizing: "border-box",
        }}
      >
        <Box className="resume-header-area">
          {renderHeader()}
        </Box>
        <Divider sx={{ borderColor: '#000', borderWidth: 1, my: 3 }} />
        {sectionOrder.map((sectionId, index) => {
          const content = renderSectionContent(sectionId);
          if (!content) return null;
          const visibleSections = sectionOrder.filter(id => renderSectionContent(id) !== null);
          const visibleIndex = visibleSections.indexOf(sectionId);
          return (
            <React.Fragment key={sectionId}>
              {content}
              {visibleIndex < visibleSections.length - 1 && <Divider sx={{ borderColor: '#ddd', my: 2 }} />}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Pages Container */}
      <Box id="resume-container">
        {exporting ? (
          // Render all pages sequentially for PDF download
          Array.from({ length: totalPages }).map((_, pageIdx) => (
            <Paper
              key={pageIdx}
              elevation={0}
              className="resume-page"
              ref={pageIdx === 0 ? resumeRef : null}
              sx={{
                width: page.width,
                height: page.height,
                bgcolor: 'white',
                mx: 'auto',
                mb: '24px',
                p: `${topBottomMargin}px ${sideMargins}px`,
                fontFamily: font,
                color: '#1a1a1a',
                boxSizing: 'border-box',
                overflow: 'hidden',
                pageBreakAfter: 'always',
                '@media print': {
                  width: page.width,
                  height: page.height,
                  p: `${topBottomMargin}px ${sideMargins}px`,
                  margin: 0,
                  boxShadow: 'none',
                  pageBreakAfter: 'always',
                }
              }}
            >
              {pageIdx === 0 && (
                <Box className="resume-header-area">
                  {renderHeader()}
                </Box>
              )}
              {pageIdx === 0 && <Divider sx={{ borderColor: '#000', borderWidth: 1, my: 3 }} />}
              {getPageSections(pageIdx).map((sectionId, index) => {
                const content = renderSectionContent(sectionId);
                if (!content) return null;
                const pageSections = getPageSections(pageIdx);
                return (
                  <React.Fragment key={sectionId}>
                    {content}
                    {index < pageSections.length - 1 && <Divider sx={{ borderColor: '#ddd', my: 2 }} />}
                  </React.Fragment>
                );
              })}
            </Paper>
          ))
        ) : (
          // Render only current page in the editor UI
          <Paper
            elevation={3}
            className="resume-page"
            ref={resumeRef}
            sx={{
              width: page.width,
              minHeight: page.height,
              bgcolor: 'white',
              mx: 'auto',
              p: `${topBottomMargin}px ${sideMargins}px`,
              fontFamily: font,
              color: '#1a1a1a',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {currentPage === 0 && (
              <Box className="resume-header-area">
                {renderHeader()}
              </Box>
            )}
            {currentPage === 0 && <Divider sx={{ borderColor: '#000', borderWidth: 1, my: 3 }} />}
            {getSectionsForCurrentPage().map((sectionId, index) => {
              const content = renderSectionContent(sectionId);
              if (!content) return null;
              const pageSections = getSectionsForCurrentPage();
              return (
                <React.Fragment key={sectionId}>
                  {content}
                  {index < pageSections.length - 1 && <Divider sx={{ borderColor: '#ddd', my: 2 }} />}
                </React.Fragment>
              );
            })}
          </Paper>
        )}
      </Box>

      {/* ── Arrange Sections Dialog ── */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} /> Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>{renderDragPanel()}</DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset Order</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume46;